import { createContext, useContext, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FavoritesContext = createContext(null);

export function FavoritesProvider({ children }) {
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data: items = [] } = useQuery({
    queryKey: ["favorites"],
    queryFn: () => api.get("/favorites").then((r) => r.data),
    enabled: !!user,
  });

  const { data: dealItems = [] } = useQuery({
    queryKey: ["favorite-deals"],
    queryFn: () => api.get("/favorites/deals").then((r) => r.data),
    enabled: !!user,
  });

  const ids = useMemo(() => new Set(items.map((i) => i.id)), [items]);
  const dealIds = useMemo(() => new Set(dealItems.map((i) => i.id)), [dealItems]);

  const toggle = async (item) => {
    if (!user) { toast.error("Log in to save trips to your wishlist"); return; }
    const saved = ids.has(item.id);
    try {
      if (saved) { await api.delete(`/favorites/${item.id}`); toast.success("Removed from Saved Trips"); }
      else { await api.post("/favorites", { travel_id: item.id }); toast.success("Saved to your trips"); }
      qc.invalidateQueries({ queryKey: ["favorites"] });
    } catch (e) { toast.error("Could not update Saved Trips"); }
  };

  const toggleDeal = async (deal) => {
    if (!user) { toast.error("Log in to save deals to your wishlist"); return; }
    const saved = dealIds.has(deal.id);
    try {
      if (saved) { await api.delete(`/favorites/deals/${deal.id}`); toast.success("Removed from Saved Deals"); }
      else { await api.post("/favorites/deals", { deal_id: deal.id }); toast.success("Saved to your deals"); }
      qc.invalidateQueries({ queryKey: ["favorite-deals"] });
    } catch (e) { toast.error("Could not update Saved Deals"); }
  };

  const hasDeal = (id) => dealIds.has(id);

  return (
    <FavoritesContext.Provider value={{ items, ids, toggle, dealItems, dealIds, toggleDeal, hasDeal, isLoggedIn: !!user }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export const useFavorites = () =>
  useContext(FavoritesContext) || { items: [], ids: new Set(), toggle: () => {}, dealItems: [], dealIds: new Set(), toggleDeal: () => {}, hasDeal: () => false, isLoggedIn: false };
